import React, { useRef, useEffect } from 'react';
import { LocationPoint } from './utils/locationHistoryUtils';
import { Loader2 } from 'lucide-react';
import { loadGoogleMapsForMap } from '@/components/couriers/map/mapService';

interface GoogleMapProps {
  locationHistory: LocationPoint[];
  isLoading?: boolean;
  onMapReady?: (map: google.maps.Map) => void;
}

const GoogleMap: React.FC<GoogleMapProps> = ({
  locationHistory = [],
  isLoading = false,
  onMapReady
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const [apiLoaded, setApiLoaded] = React.useState<boolean>(false);
  const [apiError, setApiError] = React.useState<string | null>(null);
  const [mapReady, setMapReady] = React.useState<boolean>(false);
  
  // Загрузка API Google Maps
  useEffect(() => {
    // Проверяем, загружен ли API
    if (window.google && window.google.maps) {
      setApiLoaded(true);
      return;
    }
    
    // Загружаем API используя функцию из mapService
    loadGoogleMapsForMap(
      () => {
        setApiLoaded(true);
      },
      (error) => {
        console.error('Ошибка загрузки Google Maps API:', error);
        setApiError(`Ошибка загрузки карты: ${error}`);
      }
    );
  }, []);
  
  // Функция для очистки маркеров и полилиний
  const clearMapObjects = () => {
    // Удаляем маркеры
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    // Удаляем полилинию
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
  };

  // Функция для проверки уникальности координат - ИСПРАВЛЕННАЯ ВЕРСИЯ
  const hasUniqueCoordinates = (points: LocationPoint[]): boolean => {
    if (points.length <= 1) {
      console.log('Недостаточно точек для проверки движения:', points.length);
      return false;
    }
    
    // Уменьшаем порог еще больше - примерно 1 метр
    const threshold = 0.00001;
    
    console.log('Проверяем уникальность координат с порогом:', threshold);
    console.log('Всего точек для анализа:', points.length);
    
    // Получаем первую точку как базовую
    const basePoint = points[0];
    console.log('Базовая точка:', { lat: basePoint.latitude, lng: basePoint.longitude });
    
    let uniquePointsFound = 0;
    let maxLatDiff = 0;
    let maxLngDiff = 0;
    
    // Проверяем каждую точку относительно базовой
    for (let i = 1; i < points.length; i++) {
      const currentPoint = points[i];
      const latDiff = Math.abs(currentPoint.latitude - basePoint.latitude);
      const lngDiff = Math.abs(currentPoint.longitude - basePoint.longitude);
      
      // Обновляем максимальные различия
      if (latDiff > maxLatDiff) maxLatDiff = latDiff;
      if (lngDiff > maxLngDiff) maxLngDiff = lngDiff;
      
      if (latDiff > threshold || lngDiff > threshold) {
        uniquePointsFound++;
        console.log(`Найдена уникальная точка ${i}:`, {
          point: { lat: currentPoint.latitude, lng: currentPoint.longitude },
          latDiff,
          lngDiff
        });
        
        // Если нашли хотя бы одну значительно отличающуюся точку - есть движение
        if (uniquePointsFound >= 1) {
          console.log('Движение обнаружено! Уникальных точек найдено:', uniquePointsFound);
          return true;
        }
      }
    }
    
    console.log('Статистика координат:', {
      totalPoints: points.length,
      uniquePointsFound,
      maxLatDiff,
      maxLngDiff,
      threshold
    });
    
    // Если не нашли значительных различий, но есть хотя бы минимальные изменения,
    // все равно показываем как движение
    if (maxLatDiff > 0 || maxLngDiff > 0) {
      console.log('Найдены минимальные изменения координат, показываем как движение');
      return true;
    }
    
    console.log('Движение не обнаружено - все точки идентичны');
    return false;
  };
  
  // Функция для создания маркеров и маршрута
  const createMapObjects = (map: google.maps.Map, points: LocationPoint[]) => {
    console.log('createMapObjects вызвана с', points.length, 'точками');
    
    if (points.length === 0) {
      console.log('Нет точек для отображения');
      return;
    }
    
    // Очищаем существующие объекты
    clearMapObjects();
    
    // Сортируем точки по времени
    const sortedPoints = [...points].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateA - dateB;
    });
    
    console.log('Отсортированные точки:', sortedPoints.length);
    
    // Проверяем, есть ли уникальные координаты
    const hasMovement = hasUniqueCoordinates(sortedPoints);
    console.log('Обнаружено движение:', hasMovement);
    
    // Создаем координаты для полилинии если есть движение
    if (hasMovement) {
      const path = sortedPoints.map(point => ({
        lat: point.latitude,
        lng: point.longitude
      }));
      
      console.log('Создание маршрута из', path.length, 'точек');
      
      // Создаем полилинию
      polylineRef.current = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: '#2563eb',
        strokeOpacity: 1.0,
        strokeWeight: 3
      });
      
      polylineRef.current.setMap(map);
      console.log('Полилиния добавлена на карту');
      
      // Создаем маркеры для первой и последней точек
      const firstPoint = sortedPoints[0];
      const lastPoint = sortedPoints[sortedPoints.length - 1];
      
      // Маркер начальной точки
      const startMarker = new google.maps.Marker({
        position: { lat: firstPoint.latitude, lng: firstPoint.longitude },
        map: map,
        title: 'Начальная точка',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="8" fill="#10b981" stroke="white" stroke-width="2"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(24, 24)
        }
      });
      
      const startInfoWindow = new google.maps.InfoWindow({
        content: `
          <div>
            <h3>Начальная точка</h3>
            <p>Время: ${new Date(firstPoint.timestamp).toLocaleString()}</p>
            ${firstPoint.speed !== undefined ? `<p>Скорость: ${firstPoint.speed} км/ч</p>` : ''}
            ${firstPoint.battery_level !== undefined ? `<p>Заряд: ${firstPoint.battery_level}%</p>` : ''}
          </div>
        `
      });
      
      startMarker.addListener('click', () => {
        startInfoWindow.open(map, startMarker);
      });
      
      markersRef.current.push(startMarker);
      
      // Маркер конечной точки (только если она отличается от начальной)
      if (sortedPoints.length > 1 && 
          (Math.abs(lastPoint.latitude - firstPoint.latitude) > 0.0001 || 
           Math.abs(lastPoint.longitude - firstPoint.longitude) > 0.0001)) {
        
        const endMarker = new google.maps.Marker({
          position: { lat: lastPoint.latitude, lng: lastPoint.longitude },
          map: map,
          title: 'Конечная точка',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8" fill="#ef4444" stroke="white" stroke-width="2"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(24, 24)
          }
        });
        
        const endInfoWindow = new google.maps.InfoWindow({
          content: `
            <div>
              <h3>Конечная точка</h3>
              <p>Время: ${new Date(lastPoint.timestamp).toLocaleString()}</p>
              ${lastPoint.speed !== undefined ? `<p>Скорость: ${lastPoint.speed} км/ч</p>` : ''}
              ${lastPoint.battery_level !== undefined ? `<p>Заряд: ${lastPoint.battery_level}%</p>` : ''}
            </div>
          `
        });
        
        endMarker.addListener('click', () => {
          endInfoWindow.open(map, endMarker);
        });
        
        markersRef.current.push(endMarker);
      }
      
      // Подгоняем масштаб карты под маршрут
      const bounds = new google.maps.LatLngBounds();
      path.forEach(point => bounds.extend(point));
      map.fitBounds(bounds);
      
      // Убеждаемся, что зум не слишком большой
      const listener = google.maps.event.addListener(map, "idle", () => {
        if (map.getZoom() > 16) map.setZoom(16);
        google.maps.event.removeListener(listener);
      });
    } else {
      // Если нет движения, показываем только одну точку
      const point = sortedPoints[0];
      console.log('Нет движения, показываем статичную точку:', point);
      
      const marker = new google.maps.Marker({
        position: { lat: point.latitude, lng: point.longitude },
        map: map,
        title: 'Местоположение без движения',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="8" fill="#f59e0b" stroke="white" stroke-width="2"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(24, 24)
        }
      });
      
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div>
            <h3>Статичное местоположение</h3>
            <p>Данные показывают ${sortedPoints.length} записей в одной точке</p>
            <p>Период: ${new Date(sortedPoints[0].timestamp).toLocaleString()} - ${new Date(sortedPoints[sortedPoints.length - 1].timestamp).toLocaleString()}</p>
            ${point.speed !== undefined ? `<p>Скорость: ${point.speed} км/ч</p>` : ''}
            ${point.battery_level !== undefined ? `<p>Заряд: ${point.battery_level}%</p>` : ''}
          </div>
        `
      });
      
      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });
      
      markersRef.current.push(marker);
      
      // Центрируем карту на точке
      map.setCenter({ lat: point.latitude, lng: point.longitude });
      map.setZoom(15);
    }
  };
  
  // Инициализация Google карты
  useEffect(() => {
    if (!mapRef.current || !apiLoaded) return;
    
    // Создаем карту
    const createMap = () => {
      if (!mapRef.current) return;
      
      try {
        const map = new google.maps.Map(mapRef.current, {
          zoom: 11,
          center: { lat: 55.7558, lng: 37.6173 }, // Москва по умолчанию
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          mapTypeId: google.maps.MapTypeId.ROADMAP
        });
        
        mapInstanceRef.current = map;
        console.log('Карта создана и готова');
        
        // Устанавливаем флаг готовности карты
        setMapReady(true);
        
        // Вызываем колбэк готовности карты
        if (onMapReady) {
          onMapReady(map);
        }
        
        // Если есть данные, сразу отображаем их
        if (locationHistory.length > 0) {
          console.log('Карта готова, отображаем существующие данные:', locationHistory.length, 'точек');
          createMapObjects(map, locationHistory);
        }
        
      } catch (error) {
        console.error('Ошибка при инициализации Google Maps:', error);
        setApiError(`Ошибка инициализации карты: ${error instanceof Error ? error.message : String(error)}`);
      }
    };
    
    // Если API Google Maps уже загружен, сразу создаем карту
    createMap();
    
    return () => {
      // Очистка ресурсов
      clearMapObjects();
      mapInstanceRef.current = null;
      setMapReady(false);
    };
  }, [onMapReady, apiLoaded]);
  
  // Обновление маркеров и маршрута при изменении данных
  useEffect(() => {
    console.log('locationHistory изменился:', locationHistory.length, 'точек');
    console.log('mapInstanceRef.current:', !!mapInstanceRef.current);
    console.log('mapReady:', mapReady);
    
    // Проверяем, что карта готова И есть данные
    if (mapReady && mapInstanceRef.current && locationHistory.length > 0) {
      console.log('Карта готова, создание маркеров и маршрута для', locationHistory.length, 'точек');
      createMapObjects(mapInstanceRef.current, locationHistory);
    } else if (mapReady && mapInstanceRef.current && locationHistory.length === 0) {
      // Очищаем карту если нет данных
      console.log('Очистка карты - нет данных');
      clearMapObjects();
    } else {
      console.log('Карта еще не готова или нет данных');
    }
  }, [locationHistory, mapReady]);

  return (
    <div className="w-full h-[400px] relative rounded-lg overflow-hidden bg-muted/20">
      <div ref={mapRef} className="w-full h-full" />
      
      {/* Если API не загружен или возникла ошибка */}
      {!apiLoaded && !apiError && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-muted-foreground">Загрузка Google Maps API...</p>
          </div>
        </div>
      )}
      
      {/* Если произошла ошибка */}
      {apiError && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 text-destructive">
          <div className="text-center p-4">
            <p>{apiError}</p>
            <p className="text-xs mt-1">Проверьте консоль браузера для получения подробной информации</p>
          </div>
        </div>
      )}
      
      {/* Если нет точек и не загружается, показываем сообщение */}
      {locationHistory.length === 0 && !isLoading && !apiError && apiLoaded && mapReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 text-muted-foreground">
          <div className="text-center p-4">
            <p>Нет данных о перемещениях</p>
            <p className="text-xs mt-1">Выберите другой период или проверьте наличие данных</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleMap;
